/**
 * 出片前的积分**预估**(2026-08-08 I5)。
 *
 * ★为什么要有:分身线按秒计费,而秒数由字数决定 —— 用户在写文案的时候其实是在花钱,
 *   却要到详情页才知道花了多少。写这条时页面上只有卡面一个静态标价
 *   (现价:生动版固定 168/秒 —— 0831 价表 omnihuman,用户 0926 拍板;其余档按执行口径,见下方 dhVariantOf)。
 *
 * ★★口径红线:这是**预估**,不是账单。
 *   - 单价与卡面同一个出处 —— 2026-08-26 起是**后端价目表**(`PriceBook`),不是前端常量。
 *     此前那份常量停在 ×10 时代(video 80 / 旗舰 1000 每秒),而实扣早已是 ×1.6
 *     (13 / 160)—— 这个预估器因此对着用户高报 6.25 倍;
 *   - 字数→秒数用与后端同源的 `CHARS_PER_SECOND`(4.5);
 *   - 展示必须带「预计」二字。存量教训:视频台本图报 280 实扣 700,
 *     一个说得斩钉截铁却不准的数,比不显示更糟。
 */
import type { QuoteParams } from "@/entities/pricing/api/pricingService";
import { SKU, type PriceBook } from "@/entities/pricing/model/priceBook";

import type { DhPriceVariants } from "../api/digitalHumanService";

import { estSecondsFromChars } from "./dhCreateOptions";

export interface DhCostEstimate {
  /** 估算成片/音频秒数。 */
  seconds: number;
  /** 估算积分;字数为 0 时为 0(不显示一个凭空的起步价);价目表没到货时为 null。 */
  credits: number | null;
  /** 该档单价(积分/秒),给「N 积分/秒 × M 秒」这种解释用。价目表没到货时为 null。 */
  perSecond: number | null;
}

/**
 * 档位 × 清晰度 → 价目表规格档 key —— **查后端给的表,不在前端映射**(Z2 追加 0926)。
 *
 * ★为什么:定价口径在 api 一处定(api DhCreditPrecheck.variantOf):
 *   - 照片生动版(flagship):用户 0926 拍板**固定 0831 价表 omnihuman 168/秒**,不随 ai 实际跑
 *     万相还是火山变(api DhCreditPrecheck.java:138-162);
 *   - 视频换口播 / 配音:按 ai 执行口径(gscq-ai orchestrator.py:146-159 + pricing_cache.py:339-351)。
 *   前端曾自己写死一份映射,口径一改前后端就分叉 —— 所以只认 api `GET /digital-human/price-variants`
 *   (换音色/重试服务端开单也用同一张表)。
 * ★表没到货 → null → 调用方不出数字(不兜旧映射:兜一个可能是错模型的价,比不显示更糟)。
 *
 * ★照片省流版(longform)1019 永久下架:老任务(换音色 / 重试复原原单)带 longform 一律 null = 不报价,
 *   绝不落到下面的生动版规格档(那会按生动版报一个和原单无关的价)。
 *
 * @param mode api 的档位:video / flagship / tts(longform 为已下架老值)
 * @param resolution 仅 flagship 区分;480P / 720P 各一键,其余(含缺省)落 `flagship_1080P`
 */
export function dhVariantOf(
  mode: string,
  resolution: string | null | undefined,
  variants: DhPriceVariants | null | undefined
): string | null {
  if (!variants) return null;
  if (mode === "video") return variants.video ?? null;
  if (mode === "tts") return variants.tts ?? null;
  if (mode === "longform") return null;
  const r = (resolution ?? "").toUpperCase();
  const key = r === "480P" ? "flagship_480P" : r === "720P" ? "flagship_720P" : "flagship_1080P";
  return variants[key] ?? null;
}

/**
 * @param book 价目表(`usePriceBook()`)。★ 取不到价时 `perSecond` / `credits` 都是 null ——
 *     调用方必须渲染「—」或骨架,**不许兜一个数**:一个说得斩钉截铁却不准的数,比不显示更糟。
 * @param mode api 的档位:video / flagship / tts
 * @param resolution 仅 flagship 分 480P / 720P 两个价
 */
export function estimateDhCost(
  book: PriceBook,
  mode: string,
  chars: number,
  resolution: string | undefined,
  variants: DhPriceVariants | null | undefined
): DhCostEstimate {
  const variant = dhVariantOf(mode, resolution, variants);
  const perSecond = variant == null ? null : book.unit(SKU.dhRender, variant);
  const seconds = chars > 0 ? estSecondsFromChars(chars) : 0;
  return { seconds, credits: perSecond == null ? null : seconds * perSecond, perSecond };
}

/**
 * 换音色 / 重试(复原原单重出)这一单的报价参数(修复波 0926 第八批 Z2)。
 *
 * ★与 api `DhCreditPrecheck.openReplayQuote` 逐项同源:规格档 = `dhVariantOf(原单 mode, 原单清晰度)`,
 *   量 = 原单文案字数 ÷ 4.5 向上取整。api 按这组参数在服务端开正式单并绑定,
 *   所以这里拿它问 dryRun 报价,屏幕上的数就是结算扣的那个数(除非这几秒里平台改了价)。
 * ★`mode` 用任务详情里的 `mode`(= 生效档位:视频素材已被改写成 video),不是创建页选的档。
 * ★没有文案(直传音频的老单)→ null:估不出秒数就不报数,不编一个。
 */
export function replayQuoteParams(
  mode: string | null | undefined,
  resolution: string | null | undefined,
  script: string | null | undefined,
  variants: DhPriceVariants | null | undefined
): QuoteParams | null {
  const chars = script?.trim().length ?? 0;
  if (!mode || chars <= 0) return null;
  const variantKey = dhVariantOf(mode, resolution, variants);
  if (variantKey == null) return null;
  return { skuCode: SKU.dhRender, variantKey, qty: estSecondsFromChars(chars) };
}
